// What a live grid message (comp:<id>, event 'grid') means for one viewer.
// Messages carry numbers only, never player ids, so the viewer's own numbers
// (their holds, their purchases and picks still on their way to the
// database) are recognised here and handled separately.

/**
 * @param {{ sold?: number[], held?: { n: number, e: string }[], free?: number[] }} payload
 * @param {{ holds: Set<number>, purchased: Set<number>, inFlight: Set<number> }} mine
 * @returns {{
 *   others: { sold: number[], held: { n: number, e: string }[], free: number[] },
 *   mineHeld: { n: number, e: string }[],   // the viewer's own holds, with their end time now
 *   mineChanged: boolean                     // one of the viewer's numbers changed: reload the grid
 * }}
 */
export function splitGridMessage(payload, mine) {
  const others = { sold: [], held: [], free: [] };
  const mineHeld = [];
  let mineChanged = false;
  const isMine = (n) => mine.holds.has(n) || mine.purchased.has(n);

  (payload?.sold || []).forEach((n) => {
    if (isMine(n) || mine.inFlight.has(n)) mineChanged = true;
    else others.sold.push(n);
  });
  (payload?.held || []).forEach((h) => {
    const n = h?.n;
    if (!Number.isInteger(n)) return;
    if (mine.holds.has(n)) mineHeld.push(h);
    else if (mine.purchased.has(n)) mineChanged = true;
    // A pick on its way: the reply adds it as the viewer's own.
    else if (!mine.inFlight.has(n)) others.held.push(h);
  });
  (payload?.free || []).forEach((n) => {
    if (isMine(n)) mineChanged = true;
    else others.free.push(n);
  });
  return { others, mineHeld, mineChanged };
}

/** Other players' holds ([{ number, expiresAt }]) after a message. */
export function applyToHolds(list, others) {
  const prev = list || [];
  const drop = new Set([...others.sold, ...others.free, ...others.held.map((h) => h.n)]);
  if (!drop.size) return prev;
  const next = prev.filter((r) => !drop.has(r?.number));
  others.held.forEach((h) => next.push({ number: h.n, expiresAt: h.e }));
  return next;
}

/** A list of sold numbers after a message (sold added, freed or held removed). */
export function applyToSold(list, others) {
  const prev = list || [];
  const remove = new Set([...others.free, ...others.held.map((h) => h.n)]);
  const have = new Set(prev);
  const add = others.sold.filter((n) => !have.has(n));
  if (!add.length && !prev.some((n) => remove.has(n))) return prev;
  return [...prev.filter((n) => !remove.has(n)), ...add];
}

/** The viewer's own holds with their new end times (e.g. moved to the basket). */
export function applyMyHoldTimes(list, mineHeld) {
  const prev = list || [];
  const ends = new Map(mineHeld.map((h) => [h.n, h.e]));
  let changed = false;
  const next = prev.map((r) => {
    const e = ends.get(r?.number);
    if (!e || e === r?.expiresAt) return r;
    changed = true;
    return { ...r, expiresAt: e };
  });
  return changed ? next : prev;
}
